import { Clipboard } from '@angular/cdk/clipboard';
import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  NgZone,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { RoleService } from '@app/shared/services/roles.service';
import { TerminologyService } from '@app/shared/services/terminology.service';

import {
  TerminologyContextualWorkspaceView,
  createTerminologyContextualQueryParams,
} from './terminology-contextual-navigation';
import {
  TERMINOLOGY_CONTROLLED_SELECTOR,
  TerminologyRenderIdentity,
  readTerminologyRenderIdentity,
} from './terminology-render-identity';

const TERMINOLOGY_READ = 'terminology.read';
const TERMINOLOGY_AUTHOR = 'terminology.author';
const TERMINOLOGY_CONTEXTUAL_EDIT = 'terminology.contextual_edit';
const MENU_WIDTH_PX = 320;
const MENU_HEIGHT_PX = 300;
const VIEWPORT_GUTTER_PX = 8;
const LARGE_MARKER_HEIGHT_PX = 72;
const LARGE_MARKER_AREA_PX = 100_000;
const INSPECTION_MARKER_SIZE_PX = 18;
const INSPECTION_EXCLUDED_ANCESTORS =
  'phms-terminology-context-menu, phms-terminology-nearby-action';
const INSPECTION_ANCHOR_SELECTOR = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'legend',
  'label',
  '[role="heading"]',
  '.panel-header-title-text',
  '[class$="__title"]',
  '[class*="__title "]',
].join(',');

interface TerminologyContextMenuState {
  readonly identity: TerminologyRenderIdentity;
  readonly displayText: string;
  readonly left: number;
  readonly top: number;
  readonly focusReturnTarget: HTMLElement | null;
}

interface TerminologyInspectionItem {
  readonly id: string;
  readonly identity: TerminologyRenderIdentity;
  readonly displayText: string;
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
  readonly compactOnly: boolean;
  readonly visible: boolean;
}

interface TerminologyInspectionSource {
  readonly element: HTMLElement;
  readonly compactOnly: boolean;
}

type TerminologyInspectionRefreshMode = 'full' | 'positions';

@Component({
  selector: 'phms-terminology-context-menu',
  standalone: true,
  templateUrl: './terminology-context-menu.component.html',
  styleUrl: './terminology-context-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TerminologyContextMenuComponent {
  private readonly roles = inject(RoleService);
  private readonly router = inject(Router);
  private readonly clipboard = inject(Clipboard);
  private readonly document = inject(DOCUMENT);
  private readonly terminology = inject(TerminologyService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly zone = inject(NgZone);
  private inspectionMutationObserver: MutationObserver | null = null;
  private inspectionAnimationFrame: number | null = null;
  private inspectionRefreshMode: TerminologyInspectionRefreshMode | null = null;
  private inspectionSources: readonly TerminologyInspectionSource[] = [];
  private inspectionItemsFingerprint = '';
  private readonly onInspectionScroll = (): void => {
    this.scheduleInspectionPositionRefresh();
  };

  @ViewChild('menu') private menuElement?: ElementRef<HTMLElement>;

  readonly canRead = this.roles.hasAnyPermissionSignal(TERMINOLOGY_READ);
  readonly canAuthor = this.roles.hasAnyPermissionSignal(TERMINOLOGY_AUTHOR);
  readonly canContextualEdit = this.roles.hasAnyPermissionSignal(
    TERMINOLOGY_CONTEXTUAL_EDIT,
  );
  readonly canInspect = computed(
    () => this.canRead() && this.canContextualEdit(),
  );
  readonly menuState = signal<TerminologyContextMenuState | null>(null);
  readonly copyStatus = signal<string | null>(null);
  readonly inspectionEnabled = signal(false);
  readonly inspectionTargetCount = signal(0);
  readonly inspectionItems = signal<readonly TerminologyInspectionItem[]>([]);

  constructor() {
    effect(() => {
      if (!this.canInspect() && this.inspectionEnabled()) {
        this.stopInspection();
      }
    });

    this.destroyRef.onDestroy(() => this.stopInspection());
  }

  @HostListener('document:contextmenu', ['$event'])
  onDocumentContextMenu(event: MouseEvent): void {
    // Shift+right-click is the documented escape hatch to the browser's native menu.
    if (!this.hasContextualAccess() || event.shiftKey) {
      return;
    }

    const rendering = readTerminologyRenderIdentity(event.target);
    if (!rendering) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.openMenu(
      rendering.identity,
      event.clientX,
      event.clientY,
      this.focusReturnTarget(),
    );
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    const isContextMenuKey = event.key === 'ContextMenu';
    const isShiftF10 = event.key === 'F10' && event.shiftKey;
    if (
      !this.hasContextualAccess() ||
      (!isContextMenuKey && !isShiftF10)
    ) {
      return;
    }

    const rendering = readTerminologyRenderIdentity(event.target);
    if (!rendering) {
      return;
    }

    const bounds = rendering.element.getBoundingClientRect();
    event.preventDefault();
    event.stopPropagation();
    this.openMenu(
      rendering.identity,
      bounds.left + Math.min(24, bounds.width),
      bounds.bottom + 2,
      event.target instanceof HTMLElement
        ? event.target
        : this.focusReturnTarget(),
    );
  }

  @HostListener('document:pointerdown', ['$event'])
  onDocumentPointerdown(event: PointerEvent): void {
    if (!this.menuState()) {
      return;
    }

    const menu = this.menuElement?.nativeElement;
    if (menu && event.target instanceof Node && menu.contains(event.target)) {
      return;
    }

    this.closeMenu();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.closeMenu();
    this.scheduleInspectionRefresh();
  }

  toggleInspection(): void {
    if (!this.canInspect()) {
      return;
    }

    if (this.inspectionEnabled()) {
      this.stopInspection();
      return;
    }

    this.startInspection();
  }

  openInspectionTarget(
    item: TerminologyInspectionItem,
    event: MouseEvent,
  ): void {
    if (!this.inspectionEnabled() || !this.canInspect()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    const targetBounds = event.currentTarget instanceof HTMLElement
      ? event.currentTarget
        .closest<HTMLElement>('.terminology-inspection-target')
        ?.getBoundingClientRect()
      : null;
    this.openMenu(
      item.identity,
      targetBounds
        ? targetBounds.left + Math.min(24, targetBounds.width)
        : item.left + Math.min(24, item.width),
      targetBounds ? targetBounds.bottom + 3 : item.top + item.height + 3,
      event.currentTarget instanceof HTMLElement
        ? event.currentTarget
        : this.focusReturnTarget(),
    );
  }

  refreshInspectionTargets(): void {
    if (!this.inspectionEnabled() || !this.canInspect()) {
      this.inspectionTargetCount.set(0);
      this.inspectionItems.set([]);
      this.inspectionSources = [];
      this.inspectionItemsFingerprint = '';
      return;
    }

    const viewport = this.document.defaultView;
    const viewportWidth = viewport?.innerWidth ?? 0;
    const viewportHeight = viewport?.innerHeight ?? 0;
    const markers = Array.from(
      this.document.querySelectorAll<HTMLElement>(
        TERMINOLOGY_CONTROLLED_SELECTOR,
      ),
    );
    const items: TerminologyInspectionItem[] = [];
    const sources: TerminologyInspectionSource[] = [];
    let targetCount = 0;

    markers.forEach((marker, markerIndex) => {
      if (marker.closest(INSPECTION_EXCLUDED_ANCESTORS)) {
        return;
      }

      const rendering = readTerminologyRenderIdentity(marker);
      const markerRect = readRenderedRect(marker, viewport);
      if (!rendering || !markerRect) {
        return;
      }

      targetCount++;
      const displayText = this.resolveDisplayText(rendering.identity);
      const anchor = resolveInspectionAnchor(
        marker,
        markerRect,
        displayText,
        viewport,
      );
      const anchorRect = readRenderedRect(anchor.element, viewport);
      if (!anchorRect) {
        return;
      }

      const compactOnly = anchor.compactOnly;
      const width = compactOnly
        ? INSPECTION_MARKER_SIZE_PX
        : Math.max(INSPECTION_MARKER_SIZE_PX, anchorRect.width);
      const height = compactOnly
        ? INSPECTION_MARKER_SIZE_PX
        : Math.max(INSPECTION_MARKER_SIZE_PX, anchorRect.height);
      items.push({
        id: `${rendering.identity.usageId}:${markerIndex}`,
        identity: rendering.identity,
        displayText,
        left: Math.max(1, anchorRect.left),
        top: Math.max(1, anchorRect.top),
        width,
        height,
        compactOnly,
        visible: intersectsViewport(anchorRect, viewportWidth, viewportHeight),
      });
      sources.push({
        element: anchor.element,
        compactOnly,
      });
    });

    this.inspectionSources = sources;
    const itemsFingerprint = JSON.stringify(items);
    const itemsChanged = itemsFingerprint !== this.inspectionItemsFingerprint;
    const targetCountChanged = targetCount !== this.inspectionTargetCount();
    if (itemsChanged || targetCountChanged) {
      this.inspectionItemsFingerprint = itemsFingerprint;
      this.zone.run(() => {
        this.inspectionTargetCount.set(targetCount);
        if (itemsChanged) {
          this.inspectionItems.set(items);
        }
      });
    }
  }

  onMenuKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closeMenu(true);
      return;
    }

    if (event.key === 'Tab') {
      this.closeMenu();
      return;
    }

    const menuItems = this.menuItems();
    if (menuItems.length === 0) {
      return;
    }

    const currentIndex = menuItems.indexOf(
      this.document.activeElement as HTMLButtonElement,
    );
    let nextIndex: number | null = null;
    switch (event.key) {
      case 'ArrowDown':
        nextIndex = currentIndex < menuItems.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'ArrowUp':
        nextIndex = currentIndex > 0 ? currentIndex - 1 : menuItems.length - 1;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = menuItems.length - 1;
        break;
      default:
        break;
    }

    if (nextIndex === null) {
      return;
    }

    event.preventDefault();
    menuItems[nextIndex]?.focus();
  }

  openDetails(): void {
    const state = this.menuState();
    if (!state || !this.hasContextualAccess()) {
      return;
    }

    this.navigateToTerminology(state.identity, 'technical');
  }

  editWording(): void {
    const state = this.menuState();
    if (!state || !this.hasContextualAccess() || !this.canAuthor()) {
      return;
    }

    this.navigateToTerminology(state.identity, 'edit');
  }

  viewAllUsages(): void {
    const state = this.menuState();
    if (!state || !this.hasContextualAccess()) {
      return;
    }

    this.navigateToTerminology(state.identity, 'impact');
  }

  copyToken(): void {
    const state = this.menuState();
    if (!state || !this.hasContextualAccess()) {
      return;
    }

    this.copyStatus.set(
      this.clipboard.copy(state.identity.tokenCode)
        ? `Copied ${state.identity.tokenCode}`
        : 'Unable to copy the terminology token',
    );
  }

  private openMenu(
    identity: TerminologyRenderIdentity,
    requestedLeft: number,
    requestedTop: number,
    focusReturnTarget: HTMLElement | null,
  ): void {
    const viewport = this.document.defaultView;
    const viewportWidth =
      viewport?.innerWidth ?? MENU_WIDTH_PX + VIEWPORT_GUTTER_PX * 2;
    const viewportHeight =
      viewport?.innerHeight ?? MENU_HEIGHT_PX + VIEWPORT_GUTTER_PX * 2;
    const left = Math.max(
      VIEWPORT_GUTTER_PX,
      Math.min(
        requestedLeft,
        viewportWidth - MENU_WIDTH_PX - VIEWPORT_GUTTER_PX,
      ),
    );
    const top = Math.max(
      VIEWPORT_GUTTER_PX,
      Math.min(
        requestedTop,
        viewportHeight - MENU_HEIGHT_PX - VIEWPORT_GUTTER_PX,
      ),
    );
    const state = {
      identity,
      displayText: this.resolveDisplayText(identity),
      left,
      top,
      focusReturnTarget,
    };

    this.copyStatus.set(null);
    this.menuState.set(state);
    viewport?.setTimeout(() => {
      if (this.menuState() !== state) {
        return;
      }
      this.menuItems()[0]?.focus();
    });
  }

  private closeMenu(restoreFocus = false): void {
    const returnTarget = this.menuState()?.focusReturnTarget ?? null;
    this.menuState.set(null);
    this.copyStatus.set(null);
    if (restoreFocus && returnTarget?.isConnected) {
      returnTarget.focus();
    }
  }

  private navigateToTerminology(
    identity: TerminologyRenderIdentity,
    view: TerminologyContextualWorkspaceView,
  ): void {
    this.closeMenu();
    void this.router.navigate(['/platform-admin/terminology'], {
      queryParams: createTerminologyContextualQueryParams(identity, this.router.url, view),
    });
  }

  private menuItems(): HTMLButtonElement[] {
    return Array.from(
      this.menuElement?.nativeElement.querySelectorAll<HTMLButtonElement>(
        '[role="menuitem"]',
      ) ?? [],
    );
  }

  private focusReturnTarget(): HTMLElement | null {
    return this.document.activeElement instanceof HTMLElement
      ? this.document.activeElement
      : null;
  }

  private resolveDisplayText(identity: TerminologyRenderIdentity): string {
    const resolvedText = this.terminology.resolveRendering(identity).trim();
    if (resolvedText) {
      return resolvedText;
    }

    return identity.tokenCode;
  }

  private hasContextualAccess(): boolean {
    return this.canInspect();
  }

  private startInspection(): void {
    this.inspectionEnabled.set(true);
    this.refreshInspectionTargets();
    this.zone.runOutsideAngular(() => {
      this.document.addEventListener('scroll', this.onInspectionScroll, true);

      const MutationObserverConstructor =
        this.document.defaultView?.MutationObserver;
      if (!MutationObserverConstructor || !this.document.body) {
        return;
      }

      this.inspectionMutationObserver = new MutationObserverConstructor(
        (mutations) => {
          const hasTerminologyMutation = mutations.some(
            (mutation) => isTerminologyInspectionMutation(
              mutation,
              this.hostElement.nativeElement,
            ),
          );
          if (hasTerminologyMutation) {
            this.scheduleInspectionRefresh();
          }
        },
      );
      this.inspectionMutationObserver.observe(this.document.body, {
        attributes: true,
        attributeFilter: [
          'data-terminology-controlled',
          'data-terminology-token',
          'data-terminology-usage',
          'data-terminology-configuration',
        ],
        characterData: true,
        childList: true,
        subtree: true,
      });
    });
  }

  private stopInspection(): void {
    this.inspectionEnabled.set(false);
    this.inspectionTargetCount.set(0);
    this.inspectionItems.set([]);
    this.inspectionSources = [];
    this.inspectionItemsFingerprint = '';
    this.document.removeEventListener('scroll', this.onInspectionScroll, true);
    this.inspectionMutationObserver?.disconnect();
    this.inspectionMutationObserver = null;

    const viewport = this.document.defaultView;
    if (viewport && this.inspectionAnimationFrame !== null) {
      viewport.cancelAnimationFrame(this.inspectionAnimationFrame);
    }
    this.inspectionAnimationFrame = null;
    this.inspectionRefreshMode = null;
  }

  private scheduleInspectionRefresh(): void {
    this.scheduleInspectionFrame('full');
  }

  private scheduleInspectionPositionRefresh(): void {
    this.scheduleInspectionFrame('positions');
  }

  private scheduleInspectionFrame(
    mode: TerminologyInspectionRefreshMode,
  ): void {
    if (!this.inspectionEnabled()) {
      return;
    }

    if (this.inspectionAnimationFrame !== null) {
      if (mode === 'full') {
        this.inspectionRefreshMode = 'full';
      }
      return;
    }

    const viewport = this.document.defaultView;
    if (!viewport) {
      if (mode === 'full') {
        this.refreshInspectionTargets();
      } else {
        this.refreshInspectionTargetPositions();
      }
      return;
    }

    this.inspectionRefreshMode = mode;
    this.zone.runOutsideAngular(() => {
      this.inspectionAnimationFrame = viewport.requestAnimationFrame(() => {
        const refreshMode = this.inspectionRefreshMode;
        this.inspectionAnimationFrame = null;
        this.inspectionRefreshMode = null;
        if (refreshMode === 'full') {
          this.refreshInspectionTargets();
        } else {
          this.refreshInspectionTargetPositions();
        }
      });
    });
  }

  private refreshInspectionTargetPositions(): void {
    if (!this.inspectionEnabled()) {
      return;
    }

    const inspectorHost = this.hostElement.nativeElement as HTMLElement;
    const targets = Array.from(
      inspectorHost.querySelectorAll<HTMLElement>(
        '.terminology-inspection-target',
      ),
    );
    if (targets.length !== this.inspectionSources.length) {
      this.scheduleInspectionRefresh();
      return;
    }

    const viewport = this.document.defaultView;
    const viewportWidth = viewport?.innerWidth ?? 0;
    const viewportHeight = viewport?.innerHeight ?? 0;
    const positions = this.inspectionSources.map((source) => {
      if (!source.element.isConnected) {
        return null;
      }

      const rect = source.element.getBoundingClientRect();
      const width = source.compactOnly
        ? INSPECTION_MARKER_SIZE_PX
        : Math.max(INSPECTION_MARKER_SIZE_PX, rect.width);
      const height = source.compactOnly
        ? INSPECTION_MARKER_SIZE_PX
        : Math.max(INSPECTION_MARKER_SIZE_PX, rect.height);
      return {
        height,
        left: Math.max(1, rect.left),
        top: Math.max(1, rect.top),
        visible: rect.width > 0
          && rect.height > 0
          && intersectsViewport(rect, viewportWidth, viewportHeight),
        width,
      };
    });

    if (positions.some((position) => position === null)) {
      this.scheduleInspectionRefresh();
    }

    targets.forEach((target, index) => {
      const position = positions[index];
      if (!position) {
        target.hidden = true;
        return;
      }

      target.hidden = !position.visible;
      target.style.left = `${position.left}px`;
      target.style.top = `${position.top}px`;
      target.style.width = `${position.width}px`;
      target.style.height = `${position.height}px`;
    });
  }
}

function isTerminologyInspectionMutation(
  mutation: MutationRecord,
  inspectorHost: HTMLElement,
): boolean {
  if (inspectorHost.contains(mutation.target)) {
    return false;
  }

  if (mutation.type === 'attributes') {
    return true;
  }

  const mutationParent = mutation.target instanceof Element
    ? mutation.target
    : mutation.target.parentElement;
  if (mutationParent?.closest(TERMINOLOGY_CONTROLLED_SELECTOR)) {
    return true;
  }

  return mutation.type === 'childList'
    && [
      ...Array.from(mutation.addedNodes),
      ...Array.from(mutation.removedNodes),
    ]
      .some(nodeContainsTerminologyMarker);
}

function nodeContainsTerminologyMarker(node: Node): boolean {
  return node instanceof Element
    && (
      node.matches(TERMINOLOGY_CONTROLLED_SELECTOR)
      || node.querySelector(TERMINOLOGY_CONTROLLED_SELECTOR) !== null
    );
}

function resolveInspectionAnchor(
  marker: HTMLElement,
  markerRect: DOMRect,
  displayText: string,
  viewport: Window | null,
): { element: HTMLElement; compactOnly: boolean } {
  const isLargeMarker =
    markerRect.height > LARGE_MARKER_HEIGHT_PX
    || markerRect.width * markerRect.height > LARGE_MARKER_AREA_PX;
  if (!isLargeMarker) {
    return { element: marker, compactOnly: false };
  }

  const candidates = Array.from(
    marker.querySelectorAll<HTMLElement>(INSPECTION_ANCHOR_SELECTOR),
  ).filter(
    (candidate) =>
      candidate.closest(TERMINOLOGY_CONTROLLED_SELECTOR) === marker
      && readRenderedRect(candidate, viewport) !== null,
  );
  const normalizedDisplayText = normalizeVisibleText(displayText);
  const exactCandidates = candidates.filter(
    (candidate) =>
      normalizeVisibleText(candidate.textContent ?? '') === normalizedDisplayText,
  );
  const anchor = smallestRenderedElement(
    exactCandidates.length > 0 ? exactCandidates : candidates,
  );
  if (anchor) {
    return { element: anchor, compactOnly: false };
  }

  return { element: marker, compactOnly: true };
}

function smallestRenderedElement(elements: readonly HTMLElement[]): HTMLElement | null {
  return elements.reduce<HTMLElement | null>((smallest, candidate) => {
    if (!smallest) {
      return candidate;
    }

    const candidateRect = candidate.getBoundingClientRect();
    const smallestRect = smallest.getBoundingClientRect();
    return candidateRect.width * candidateRect.height
        < smallestRect.width * smallestRect.height
      ? candidate
      : smallest;
  }, null);
}

function readRenderedRect(
  element: HTMLElement,
  viewport: Window | null,
): DOMRect | null {
  const style = viewport?.getComputedStyle(element);
  if (
    style?.display === 'none'
    || style?.visibility === 'hidden'
    || style?.visibility === 'collapse'
  ) {
    return null;
  }

  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0 ? rect : null;
}

function intersectsViewport(
  rect: DOMRect,
  viewportWidth: number,
  viewportHeight: number,
): boolean {
  return (
    rect.right > 0
    && rect.bottom > 0
    && rect.left < viewportWidth
    && rect.top < viewportHeight
  );
}

function normalizeVisibleText(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}
